import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { PageLoader } from "@/components/common/Loading";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { PersonSearchSelect } from "@/components/common/PersonSearchSelect";
import { FileSearch, ShieldAlert, XCircle } from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTS = [
  { v: "all", l: "All Statuses" }, { v: "unmatched", l: "Unmatched" }, { v: "needs_review", l: "Needs Review" },
  { v: "error", l: "Error" }, { v: "duplicate", l: "Duplicate" },
  { v: "matched_manually", l: "Matched Manually" }, { v: "ignored", l: "Ignored" },
];
const CARD = [
  ["total_staged", "Total Staged", "text-slate-900"], ["matched", "Matched", "text-emerald-600"],
  ["unmatched", "Unmatched", "text-amber-600"], ["needs_review", "Needs Review", "text-amber-600"],
  ["duplicates", "Duplicates", "text-sky-600"], ["errors", "Errors", "text-rose-600"],
  ["resolved", "Resolved", "text-emerald-600"], ["ignored", "Ignored", "text-slate-400"],
];
const badgeStatus = (s) => ({ unmatched: "warning", needs_review: "warning", error: "inactive",
  duplicate: "info", matched_manually: "active", ignored: "inactive" }[s] || "inactive");

export default function AttachmentReview() {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState(null);
  const [batches, setBatches] = useState([]);
  const [status, setStatus] = useState("all");
  const [batch, setBatch] = useState("all");
  const [q, setQ] = useState("");
  const [resolveItem, setResolveItem] = useState(null);
  const [ignoreItem, setIgnoreItem] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (status !== "all") params.status = status;
      if (batch !== "all") params.batch = batch;
      if (q) params.q = q;
      const [s, l, b] = await Promise.all([
        api.get("/migration/attachments/review/summary", { params: batch !== "all" ? { batch } : {} }),
        api.get("/migration/attachments/review", { params }),
        api.get("/migration/attachments/batches"),
      ]);
      setSummary(s.data); setItems(l.data.items); setBatches(b.data.items);
    } catch (e) { toast.error(apiError(e)); setItems([]); }
  }, [status, batch, q]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="attachment-review-page">
      <PageHeader title="Attachment Review" description="Legacy attachment migration exceptions. No file is ever silently skipped or guessed — unmatched items land here for manual association." />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="migration-summary">
        {CARD.map(([k, label, color]) => (
          <div key={k} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`summary-${k}`}>
            <div className={`text-2xl font-bold ${color}`}>{summary ? (summary[k] ?? 0) : "—"}</div>
            <div className="text-xs font-medium text-slate-500 mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="w-48">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="review-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUS_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="w-56">
          <Select value={batch} onValueChange={setBatch}>
            <SelectTrigger data-testid="review-batch-filter"><SelectValue placeholder="All Batches" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Batches</SelectItem>
              {batches.map((b) => <SelectItem key={b.id} value={b.id}>{b.human_id} · {b.source_system || "—"}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search file / legacy id / import id" className="w-72" data-testid="review-search" />
      </div>

      {items === null ? <PageLoader /> : items.length === 0 ? (
        <EmptyState icon={FileSearch} title="No review items" description="Attachments that cannot be safely matched during a migration batch will appear here for manual association." />
      ) : (
        <div className="overflow-x-auto tr-scroll rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full text-sm" data-testid="review-table">
            <thead><tr className="text-left text-xs font-semibold text-slate-500 border-b border-slate-200 bg-slate-50">
              <th className="py-2.5 px-3">Review ID</th><th className="py-2.5 px-3">File</th><th className="py-2.5 px-3">Legacy ID</th>
              <th className="py-2.5 px-3">Import ID</th><th className="py-2.5 px-3">Category</th><th className="py-2.5 px-3">Visibility</th>
              <th className="py-2.5 px-3">Status</th><th className="py-2.5 px-3">Reason / Resolution</th><th className="py-2.5 px-3"></th>
            </tr></thead>
            <tbody>
              {items.map((r) => {
                const active = ["unmatched", "needs_review", "error"].includes(r.status);
                return (
                  <tr key={r.id} className="border-b border-slate-100 align-top" data-testid={`review-row-${r.id}`}>
                    <td className="py-2.5 px-3 font-medium text-slate-800">{r.human_id}</td>
                    <td className="py-2.5 px-3 text-slate-700 max-w-xs truncate" title={r.original_filename}>{r.original_filename || "—"}</td>
                    <td className="py-2.5 px-3 text-slate-500">{r.legacy_attachment_id || "—"}</td>
                    <td className="py-2.5 px-3 text-slate-500 max-w-[9rem] truncate" title={r.person_import_id}>{r.person_import_id || "—"}</td>
                    <td className="py-2.5 px-3 text-slate-600">{r.document_category || "—"}</td>
                    <td className="py-2.5 px-3"><StatusBadge status="inactive" label={(r.visibility || "").replace("_", " ") || "—"} /></td>
                    <td className="py-2.5 px-3"><StatusBadge status={badgeStatus(r.status)} label={r.status.replace("_", " ")} /></td>
                    <td className="py-2.5 px-3 text-slate-500 max-w-xs">
                      {r.status === "matched_manually" && r.final_person ? `→ ${r.final_person.name} (${r.final_person.human_id})` : (r.match_failure_reason || r.resolution_notes || "—")}
                    </td>
                    <td className="py-2.5 px-3 whitespace-nowrap">
                      {active && (
                        <div className="flex gap-1.5">
                          <Button size="sm" onClick={() => setResolveItem(r)} data-testid={`review-resolve-${r.id}`} className="bg-[#006090] hover:bg-[#004E75]">Associate</Button>
                          <Button size="sm" variant="outline" onClick={() => setIgnoreItem(r)} data-testid={`review-ignore-${r.id}`}>Ignore</Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ResolveDialog item={resolveItem} onClose={() => setResolveItem(null)} onDone={() => { setResolveItem(null); load(); }} />
      <IgnoreDialog item={ignoreItem} onClose={() => setIgnoreItem(null)} onDone={() => { setIgnoreItem(null); load(); }} />
    </div>
  );
}

function ResolveDialog({ item, onClose, onDone }) {
  const [person, setPerson] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setPerson(null); setNotes(""); }, [item]);
  if (!item) return null;
  const submit = async () => {
    if (!person) return toast.error("Select a Person to associate");
    setBusy(true);
    try {
      const { data } = await api.post(`/migration/attachments/review/${item.id}/resolve`, { person_id: person.id, resolution_notes: notes || null });
      toast.success(`Associated to ${data.final_person.name}${data.duplicate ? " (duplicate prevented)" : ""}`);
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white" data-testid="resolve-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-[#006090]" /> Manually Associate Attachment</DialogTitle>
          <DialogDescription>{item.original_filename} · Legacy {item.legacy_attachment_id || "—"}. Matching follows your manual selection; Person data is not modified from the file.</DialogDescription></DialogHeader>
        <PersonSearchSelect onSelect={setPerson} selected={person} testId="resolve-person-search" />
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Resolution notes (optional)" data-testid="resolve-notes" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !person} data-testid="resolve-confirm" className="bg-[#006090] hover:bg-[#004E75]">Confirm Association</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IgnoreDialog({ item, onClose, onDone }) {
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setNotes(""); }, [item]);
  if (!item) return null;
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/migration/attachments/review/${item.id}/ignore`, { resolution_notes: notes || null });
      toast.success("Marked as ignored / not applicable");
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white" data-testid="ignore-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><XCircle className="w-5 h-5 text-slate-500" /> Ignore Migration Item</DialogTitle>
          <DialogDescription>The file remains stored with full provenance; it is simply removed from the active queue.</DialogDescription></DialogHeader>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Why is this not applicable? (optional)" data-testid="ignore-notes" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="ignore-confirm" className="bg-slate-700 hover:bg-slate-800">Confirm Ignore</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
